import type { IconName } from '../components/ui/Icon';
import type { ModalKind } from '../stores/modal.store';
import { ModuleKey } from '@masamune/shared';
import { isFrozen } from './frozen';

/**
 * Fonte unica delle creazioni ("+ Nuovo" in alto e azioni rapide della ricerca).
 *
 * La Topbar costruisce i suoi gruppi da `NEW_ACTIONS`, e la palette le sue
 * azioni rapide di creazione dalla STESSA lista, filtrata sui permessi.
 *
 * 26/10/2026: parole riscritte in italiano semplice (prima "Quattro domande",
 * "Anagrafica CRM", "Proposta firmabile (eIDAS)", "Documento fiscale
 * numerato", gruppo "Back-office"). Ogni sottotitolo dice cosa si crea, non
 * come è fatto dentro. I gruppi hanno i nomi della barra laterale.
 */

/** Raggruppamento mostrato nel NewMenu della Topbar. */
export type NewActionGroup = 'Clienti e lavori' | 'Documenti per i clienti' | 'Ufficio';

export interface NewAction {
  /** Chiave stabile (usata anche come id `qa-*` nella palette). */
  key: string;
  /** Titolo voce (NewMenu) / label (palette). */
  title: string;
  /** Sottotitolo: cosa si crea, in parole. */
  sub: string;
  /** Icona NewMenu. */
  icon: IconName;
  /** Modale da aprire al click. */
  modal: Exclude<ModalKind, null>;
  /**
   * Moduli che abilitano la voce: visibile se l'utente ha WRITE su ALMENO uno
   * di essi (es. Progetto = PRODUCT|MARKETING|DESIGN).
   */
  module: ModuleKey[];
  /** Gruppo nel NewMenu della Topbar. */
  group: NewActionGroup;
  /** Colore del cerchio icona nella palette (var(--xxx)). */
  paletteColor: string;
  /** Label mostrata nella palette ("Nuovo cliente", …). */
  paletteLabel: string;
  /** Sottotitolo mostrato nella palette. */
  paletteSub: string;
  /**
   * Fuori dal "+ Nuovo" e dalla ricerca (menu alleggerito, Lorenzo 26/10/2026):
   * la creazione resta dove serve, nella sua pagina. Togliere il flag la rimette.
   */
  nascosta?: boolean;
}

/** Le voci, nell'ordine in cui si usano: prima quello che si crea ogni giorno. */
export const NEW_ACTIONS: NewAction[] = [
  // ── Clienti e lavori ──
  {
    key: 'new-task',
    title: 'Task',
    sub: 'Cosa fare, chi lo fa, entro quando',
    icon: 'tasks',
    modal: 'newTask',
    module: [ModuleKey.TASKS],
    group: 'Clienti e lavori',
    paletteColor: 'var(--accent)',
    paletteLabel: 'Nuovo task',
    paletteSub: 'Cosa fare, chi lo fa, entro quando',
  },
  {
    key: 'new-deal',
    title: 'Trattativa',
    sub: 'Voucher, rinnovo, estensione o cliente nuovo',
    icon: 'handshake',
    modal: 'newDeal',
    module: [ModuleKey.TRATTATIVE],
    group: 'Clienti e lavori',
    paletteColor: 'var(--accent)',
    paletteLabel: 'Nuova trattativa',
    paletteSub: 'Voucher, rinnovo, estensione o cliente nuovo',
  },
  {
    key: 'new-client',
    title: 'Cliente',
    sub: "Un'azienda o una persona nei Contatti",
    icon: 'users',
    modal: 'newClient',
    module: [ModuleKey.CRM],
    group: 'Clienti e lavori',
    paletteColor: 'var(--brand-marketing)',
    paletteLabel: 'Nuovo cliente',
    paletteSub: "Un'azienda o una persona nei Contatti",
  },
  {
    key: 'new-project',
    title: 'Progetto',
    sub: 'Per un cliente: cosa si vende e quando si consegna',
    icon: 'cube',
    modal: 'newProject',
    module: [ModuleKey.PRODUCT, ModuleKey.MARKETING, ModuleKey.DESIGN],
    group: 'Clienti e lavori',
    paletteColor: 'var(--brand-product)',
    paletteLabel: 'Nuovo progetto',
    paletteSub: 'Per un cliente: cosa si vende e quando si consegna',
  },
  {
    key: 'new-vendor',
    title: 'Professionista',
    sub: 'Un fornitore o un freelance che lavora con noi',
    icon: 'brief',
    modal: 'newVendor',
    module: [ModuleKey.SUPPLIERS],
    group: 'Clienti e lavori',
    paletteColor: 'var(--ink)',
    paletteLabel: 'Nuovo professionista',
    paletteSub: 'Un fornitore o un freelance che lavora con noi',
    // Si crea dai Contatti ("Nuovo contatto" › Professionista).
    nascosta: true,
  },
  // ── Documenti per i clienti ──
  {
    key: 'new-quote',
    title: 'Preventivo',
    sub: 'Da mandare al cliente, che lo firma online',
    icon: 'file',
    modal: 'newQuote',
    module: [ModuleKey.FATTURAZIONE],
    group: 'Documenti per i clienti',
    paletteColor: 'var(--info)',
    paletteLabel: 'Nuovo preventivo',
    paletteSub: 'Da mandare al cliente, che lo firma online',
  },
  {
    key: 'new-invoice',
    title: 'Fattura',
    sub: 'Emessa con Fatture in Cloud',
    icon: 'invoice',
    modal: 'newInvoice',
    module: [ModuleKey.FATTURAZIONE],
    group: 'Documenti per i clienti',
    paletteColor: 'var(--ok)',
    paletteLabel: 'Nuova fattura',
    paletteSub: 'Emessa con Fatture in Cloud',
  },
  // ── Ufficio ──
  {
    key: 'new-cash-entry',
    title: 'Contanti',
    sub: 'Un incasso o una spesa pagata in contanti',
    icon: 'banknote',
    modal: 'newCashEntry',
    module: [ModuleKey.AMMINISTRAZIONE],
    group: 'Ufficio',
    paletteColor: 'var(--ink)',
    paletteLabel: 'Nuovo movimento in contanti',
    paletteSub: 'Un incasso o una spesa pagata in contanti',
  },
  {
    key: 'new-tax',
    title: 'Scadenza fiscale',
    sub: 'F24, IVA, INPS o una rata da pagare',
    icon: 'calendarClock',
    modal: 'newTax',
    module: [ModuleKey.AMMINISTRAZIONE],
    group: 'Ufficio',
    paletteColor: 'var(--ink)',
    paletteLabel: 'Nuova scadenza fiscale',
    paletteSub: 'F24, IVA, INPS o una rata da pagare',
  },
  {
    key: 'attach-subscription',
    title: 'Abbonamento',
    sub: 'Link di pagamento Stripe: il cliente si abbona da solo',
    icon: 'repeat',
    modal: 'attachSubscription',
    module: [ModuleKey.SERVICES],
    group: 'Ufficio',
    paletteColor: 'var(--info)',
    paletteLabel: 'Nuovo abbonamento',
    paletteSub: 'Link di pagamento Stripe per il cliente',
  },
  {
    key: 'new-vault',
    title: 'Password',
    sub: 'Un accesso da salvare nel Password Vault',
    icon: 'lock',
    modal: 'newVault',
    module: [ModuleKey.VAULT],
    group: 'Ufficio',
    paletteColor: 'var(--ink)',
    paletteLabel: 'Nuova password nel Vault',
    paletteSub: 'Un accesso da salvare nel Password Vault',
    // Si crea dal Password Vault ("Nuova voce").
    nascosta: true,
  },
];

/** Ordine dei gruppi nel NewMenu della Topbar. */
export const NEW_ACTION_GROUP_ORDER: NewActionGroup[] = ['Clienti e lavori', 'Documenti per i clienti', 'Ufficio'];

/**
 * Filtra le voci visibili dato un predicato di permesso (canWrite per modulo).
 * Una voce è visibile se l'utente può scrivere su almeno uno dei suoi moduli.
 * FASE 0: i moduli in pausa (lib/frozen.ts) non contano — una voce i cui
 * moduli sono tutti congelati sparisce da "+ Nuovo" e dalla palette.
 */
export function visibleNewActions(
  actions: NewAction[],
  canWrite: (module: ModuleKey) => boolean,
): NewAction[] {
  return actions.filter((a) => !a.nascosta && a.module.some((m) => !isFrozen(m) && canWrite(m)));
}
